import { ChangeDetectionStrategy, Component, computed, input, inject } from '@angular/core';
import { Timeline } from 'primeng/timeline';
import { Application } from '../../model/application';
import { ApartmentDto } from 'homey-housing-lottery-app-dto';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'homey-lottery-timeline',
  imports: [Timeline],
  templateUrl: './lottery-timeline.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LotteryTimelineComponent {
  public readonly lottery = input.required<ApartmentDto>();
  public readonly application = input.required<Application>();

  private readonly t = inject(TranslocoService);

  private formatShortDateOrUndefined(date: string | undefined): string | undefined {
    if (!date) return undefined;
    // Keep non-breaking space between month and day
    return new Date(date)
      .toLocaleDateString('default', { month: 'short', day: 'numeric' })
      .replace(' ', '\u00A0');
  }

  protected readonly timelineItems = computed(() => {
    const lottery = this.lottery();
    const application = this.application();

    // Shortcuts for translated relative-time labels
    const followingWeeks  = this.t.translate('common.time.followingWeeks');
    const followingMonths = this.t.translate('common.time.followingMonths');
    const comingWeeks     = this.t.translate('common.time.comingWeeks');
    const comingMonths    = this.t.translate('common.time.comingMonths');

    // Status labels
    const S = (k: string, params?: Record<string, unknown>) =>
      this.t.translate(`apartment.timeline.status.${k}`, params);

    switch (application.status) {
      case 'not-synced':
        return [
          { status: S('applicationSubmitted'),                                                               icon: 'pi pi-envelope',  color: 'bg-blue-500' },
          { status: S('lotteryCloses'),                date: this.formatShortDateOrUndefined(lottery.endDate), icon: 'pi pi-clock',     color: 'bg-blue-400 animate-pulse' },
          { status: S('receiveLotteryNumber'),         date: followingWeeks,                                   icon: 'pi pi-receipt',   color: 'bg-gray-400' },
          { status: S('selection'),                    date: followingMonths,                                  icon: 'pi pi-file-check',color: 'bg-gray-400' }
        ];

      case 'applied':
        switch (lottery.status) {
          case 'open':
            return [
              { status: S('applicationSubmitted'),      icon: 'pi pi-envelope',  color: 'bg-blue-500' },
              { status: S('lotteryCloses'),             date: this.formatShortDateOrUndefined(lottery.endDate),       icon: 'pi pi-clock',     color: 'bg-blue-400 animate-pulse' },
              { status: S('receiveLotteryNumber'),      date: followingWeeks,                                         icon: 'pi pi-receipt',   color: 'bg-gray-400' },
              { status: S('selection'),                 date: followingMonths,                                        icon: 'pi pi-file-check',color: 'bg-gray-400' }
            ];
          case 'closed':
            return [
              { status: S('applied'),                   date: this.formatShortDateOrUndefined(application.appliedAt), icon: 'pi pi-envelope',  color: 'bg-blue-500' },
              { status: S('lotteryClosed'),             date: this.formatShortDateOrUndefined(lottery.endDate),       icon: 'pi pi-clock',     color: 'bg-blue-500' },
              { status: S('receiveLotteryNumber'),      date: comingWeeks,                                            icon: 'pi pi-receipt',   color: 'bg-blue-400 animate-pulse' },
              { status: S('selection'),                 date: followingMonths,                                        icon: 'pi pi-file-check',color: 'bg-gray-400' }
            ];
        }
        break;

      case 'pending-selection':
        return [
          { status: S('applied'),                      date: this.formatShortDateOrUndefined(application.appliedAt),           icon: 'pi pi-envelope', color: 'bg-blue-500' },
          { status: S('lotteryClosed'),                date: this.formatShortDateOrUndefined(lottery.endDate),                 icon: 'pi pi-clock',    color: 'bg-blue-500' },
          { status: S('lotteryNumber', { number: application.applicationNumber }), date: this.formatShortDateOrUndefined(application.receivedApplicationNumberAt), icon: 'pi pi-receipt', color: 'bg-blue-500' },
          { status: S('selection'),                    date: comingMonths,                                                     icon: 'pi pi-file-check', color: 'bg-blue-400 animate-pulse' }
        ];

      case 'not-selected':
        return [
          { status: S('applied'),                      date: this.formatShortDateOrUndefined(application.appliedAt),           icon: 'pi pi-envelope', color: 'bg-blue-500' },
          { status: S('lotteryClosed'),                date: this.formatShortDateOrUndefined(lottery.endDate),                 icon: 'pi pi-clock',    color: 'bg-blue-500' },
          { status: S('lotteryNumberReceived'),        date: this.formatShortDateOrUndefined(application.receivedApplicationNumberAt), icon: 'pi pi-receipt', color: 'bg-blue-500' },
          { status: S('notSelected'),                  date: this.formatShortDateOrUndefined(application.selectionAt),         icon: 'pi pi-times',    color: 'bg-red-500' }
        ];

      case 'selected':
        return [
          { status: S('applied'),                      date: this.formatShortDateOrUndefined(application.appliedAt),           icon: 'pi pi-envelope', color: 'bg-blue-500' },
          { status: S('lotteryClosed'),                date: this.formatShortDateOrUndefined(lottery.endDate),                 icon: 'pi pi-clock',    color: 'bg-blue-500' },
          { status: S('lotteryNumberReceived'),        date: this.formatShortDateOrUndefined(application.receivedApplicationNumberAt), icon: 'pi pi-receipt', color: 'bg-blue-500' },
          { status: S('selected'),                     date: this.formatShortDateOrUndefined(application.selectionAt),         icon: 'pi pi-check',    color: 'bg-green-500' }
        ];
    }

    return []; // fallback
  });
}
